import { Link } from 'react-router'

import { InternalSelectItem, SelectItem, SelectItemGroup } from './types'

function getCollectionItem<ValueType>(
  item: SelectItem<ValueType>,
  index: number | string
): InternalSelectItem<SelectItem<ValueType>> {
  return {
    ...item,
    id: `item-${index}-${String(item.value).replace(/\W/g, '')}`,
    value: item,
    children: item.label,
  }
}

export function processItem<ValueType>(item: SelectItem<ValueType> | SelectItemGroup<ValueType>, index: number) {
  if (isSelectItemGroup(item)) {
    return item.items.map((innerItem, innerIndex) => ({
      ...getCollectionItem(innerItem, `${index}-${innerIndex}`),
      group: item.label ?? '',
    })) as InternalSelectItem<SelectItem<ValueType>>[]
  }

  return getCollectionItem(item, index)
}

export function getSelectItemProps(item: SelectItem<unknown>, inRouterContext?: boolean) {
  if (item.href) {
    if (inRouterContext) return { as: Link, to: item.href, reloadDocument: item.reloadDocument }
    else return { as: 'a', href: item.href }
  }

  return {}
}

export function isSelectItem<ValueType>(item: unknown): item is SelectItem<ValueType> {
  return !!(item?.hasOwnProperty('label') && item?.hasOwnProperty('value')) && !item?.hasOwnProperty('items')
}

export function isSelectItemGroup<ValueType>(item: unknown): item is SelectItemGroup<ValueType> {
  return !!item?.hasOwnProperty('items')
}

function matchesSearchTerm<ValueType>(item: SelectItem<ValueType>, searchTerm: string): boolean {
  const lowerSearch = searchTerm.toLowerCase()

  return (
    (typeof item.value === 'string' && item.value.toLowerCase().includes(lowerSearch)) ||
    item.label.toLowerCase().includes(lowerSearch) ||
    (isSelectItem(item.value) && matchesSearchTerm(item.value, searchTerm))
  )
}

function isSameItem(obj1: unknown, obj2?: unknown) {
  if (isSelectItem(obj1) && isSelectItem(obj2)) return isSameItem(obj1.value, obj2.value)
  else if (isSelectItem(obj1)) return isSameItem(obj1.value, obj2)
  return obj1 === obj2
}

/**
 * Used in combobox to find matching items
 */
export function filterSelectOptions<ValueType, ItemType extends SelectItem<ValueType> | SelectItemGroup<ValueType>>(
  items: ItemType[] | undefined,
  searchTerm: string
) {
  const finalResults: ItemType[] = []

  items?.forEach((item) => {
    if (isSelectItemGroup(item)) {
      const filtered = item.items.filter((option) => matchesSearchTerm(option, searchTerm))
      if (filtered.length) finalResults.push({ ...item, items: filtered })
    } else if (matchesSearchTerm(item, searchTerm)) {
      finalResults.push(item)
    }
  })

  return finalResults
}

/**
 * Used in select to allow users to pass any kind of valid value
 */
export function findSelectedValue<ValueType, ItemType>(
  items: ItemType[] = [],
  value?: ValueType | SelectItem<ValueType>
) {
  for (const item of items) {
    if (isSelectItemGroup(item)) {
      const foundItem = item.items.find((option) => isSameItem(option, value)) as SelectItem<ValueType>
      if (foundItem) return foundItem
    } else if (isSameItem(item, value)) {
      return item as SelectItem<ValueType>
    }
  }
}

export type ListItemOrGroup<ValueType> =
  | InternalSelectItem<ValueType>
  | { id: string; label: string; items: InternalSelectItem<ValueType>[] }

export function groupSelectItems<ValueType>(items: Array<InternalSelectItem<ValueType>>): ListItemOrGroup<ValueType>[] {
  if (items?.[0]?.group === undefined) return items

  const groups: Record<string, InternalSelectItem<ValueType>[]> = {}

  for (const item of items) {
    const groupKey = item.group!
    if (!Array.isArray(groups[groupKey])) groups[groupKey] = []

    groups[groupKey].push(item)
  }

  return Object.entries(groups).map(([label, items], index) => ({
    id: `group-${index}`,
    label,
    items,
  }))
}
